/* eslint @typescript-eslint/no-explicit-any: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */

import QuestionSerializer from "./serializers/QuestionSerializer";
import { apiSlice } from "./apiSlice";
import { Question } from "../../app/types/Question";
import { extractTags } from "../../common/utils/helper";
import type { ListResponse } from "../../common/utils/types";

const questionSerializer = new QuestionSerializer();

interface QuestionsQueryParams {
    q?: string;
    sort?: string;
    filter?: string;
    page?: number;
}

const questionApi = apiSlice.injectEndpoints({
    endpoints: (build) => ({
        getQuestions: build.query<ListResponse<Question>, QuestionsQueryParams>({
            query: (arg) => {
                // extract tags and query from search query
                const [tags, query] = extractTags(arg.q || "");
                const params = {
                    search: query.trim() || undefined,
                    sort: arg.sort,
                    filter: arg.filter,
                    page: arg.page,
                    tags: tags.trim() || undefined,
                };
                return { url: `/NI-api/questions`, params: params };
            },
            transformResponse: (response: ListResponse<any>) => ({
                ...response,
                results: questionSerializer.deserializeList(response.results),
            }),
            providesTags: (result, error, arg) => {
                type QuestionTag = { type: "Question"; id: number };
                const tags: QuestionTag[] = result ? result.results.map(({ id }) => ({ type: "Question", id })) : [];
                return ["Question", ...tags];
            },
        }),
        getQuestion: build.query<Question, number>({
            query: (id) => ({ url: `/NI-api/question/${id}/` }),
            transformResponse: (response) => questionSerializer.deserialize(response),
            providesTags: (result, error, arg) => [{ type: "Question", id: arg }],
        }),
        createQuestion: build.mutation<number, Question>({
            query: (question) => ({
                url: `/NI-api/questions`,
                method: "POST",
                data: questionSerializer.serialize(question),
            }),
            transformResponse: (response: any) => response.id,
            invalidatesTags: ["Question"],
        }),
        updateQuestion: build.mutation<void, Question>({
            query: (question) => ({
                url: `/NI-api/question/${question.id}/`,
                method: "PUT",
                data: questionSerializer.serialize(question),
            }),
            invalidatesTags: (result, error, arg) => [{ type: "Question", id: arg.id }],
            async onQueryStarted(question, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    questionApi.util.updateQueryData("getQuestion", question.id, (q) => {
                        Object.assign(q, question);
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
        deleteQuestion: build.mutation<void, number>({
            query: (id) => ({ url: `/NI-api/question/${id}`, method: "DELETE" }),
            invalidatesTags: ["Question"],
        }),
        voteQuestion: build.mutation<void, { question: Question; vote: number }>({
            query: ({ question, vote }) => ({
                url: `/NI-api/question/${question.id}/${vote > 0 ? "like" : "dislike"}`,
                method: "POST",
            }),
            async onQueryStarted({ question, vote }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    questionApi.util.updateQueryData("getQuestion", question.id, (q) => {
                        q.votes -= q.userVote;
                        if (vote === q.userVote) {
                            q.userVote = 0;
                        } else {
                            q.votes += vote;
                            q.userVote = vote;
                        }
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patchResult.undo();
                }
            },
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetQuestionsQuery,
    useGetQuestionQuery,
    useCreateQuestionMutation,
    useUpdateQuestionMutation,
    useDeleteQuestionMutation,
    useVoteQuestionMutation,
} = questionApi;
